// @flow
import type { Dispatch } from 'redux';
import { finectAPI } from '@finect/front-utils/finectAPI';
import { getUrlParameter } from '@finect/front-utils/getUrlParameter';
import { getURLWithQueryString } from '@finect/front-utils/getURLWithQueryString';
import { isBrowser } from '@finect/front-utils/isBrowser';
import ACTIONS from '../constants';
import { getProducts } from '../products/actions';
import { shuffle } from '../../utils';
import { fetchSetting } from '../settings/actions';
import { setError } from './../error/actions';
import { lightAuthor, lightOrganization } from './../home/light';
import { models } from './../../utils/models';
import type { ReviewsOrderBy } from './types';
import { aliasWithTagContent } from './../../scenes/Articles/data';

export function setWidgets(widgets: Array<Object>) {
  return {
    type: 'SET_WIDGETS',
    widgets
  };
}

export function setWidgetsFetchingStatus(fetching: boolean) {
  return {
    type: 'SET_WIDGETS_FETCHING_STATUS',
    fetching
  };
}

let widgetsInflightPromise = null;

export function getWidgets(type?: string) {
  return function (dispatch: Dispatch, getState: Function) {
    const widgets = getState().content.widgets;
    if (widgets.items.length) return Promise.resolve(widgets.items);

    if (widgetsInflightPromise) return widgetsInflightPromise;

    dispatch(setWidgetsFetchingStatus(true));
    widgetsInflightPromise = finectAPI({
      version: 'v4',
      method: 'GET',
      path: `contents/widgets${type ? `?type=${type}` : ''}`,
      onSuccess: ({ data }: { data: Array<Object> }) => {
        dispatch(setWidgets(data));
        return data;
      },
      onError: () => dispatch(setWidgetsFetchingStatus(false))
    }).finally(() => {
      widgetsInflightPromise = null;
    });
    return widgetsInflightPromise;
  };
}

// Detalle de tag
export function setTagFetchingStatus(id: string, fetching: boolean, fetched: boolean) {
  return {
    type: ACTIONS.SET_TAG_FETCHING_STATUS,
    id,
    fetching,
    fetched
  };
}

export function setTag(tag: Object | null) {
  return {
    type: ACTIONS.SET_TAG,
    tag
  };
}

// Lista de tags
export function setTags(tags: Array<Object>) {
  return {
    type: 'SET_TAGS',
    tags
  };
}

/**
 * Obtiene una tag a partir de su identificador (ID o alias)
 *
 * @param {string} alias alias de la tag que se desea cargar
 */
export function getTag(alias: string) {
  return function (dispatch: Dispatch) {
    return Promise.all([dispatch(setTagFetchingStatus(alias, true, true))])
      .then(() =>
        finectAPI({
          version: 'v4',
          path: `contents/tags/${alias}`,
          method: 'GET',
          onSuccess: ({ data }: { data: Object }) => Promise.resolve(dispatch(setTag(data))),
          onError: () => Promise.resolve(dispatch(setTag(null)))
        }))
      .finally(() => Promise.resolve(dispatch(setTagFetchingStatus(alias, false, true))));
  };
}

export function clearTag() {
  return {
    type: ACTIONS.CLEAR_TAG
  };
}

export function setTagContentsFetchingStatus(
  contentType: 'videos',
  entityId: string,
  fetching: boolean,
  fetched: boolean
) {
  return {
    type: ACTIONS.SET_TAG_CONTENTS_FETCHING_STATUS,
    contentType,
    entityId,
    fetching,
    fetched
  };
}

// GET para obtener vídeos de una tag
// path: `contents/articles?filter=tag+eq+${tagId}+and+type+eq+'video'&orderby=-revision/updated`,

// ----->
export function setArticle(article: Object) {
  return {
    type: 'SET_ARTICLE',
    article
  };
}

export function clearArticle() {
  return {
    type: 'CLEAR_ARTICLE'
  };
}

export function setArticleNotFound() {
  return {
    type: 'SET_ARTICLE_NOT_FOUND'
  };
}

export function setArticleComments(comments: Array<Object>, paging: Object) {
  return {
    type: 'SET_ARTICLE_COMMENTS',
    comments,
    paging
  };
}

export function setArticleUserComment(comment: Object) {
  return {
    type: 'SET_ARTICLE_USER_COMMENT',
    comment
  };
}

export function setArticleCommentsFetchingStatus(fetching: boolean, fetched: boolean) {
  return {
    type: 'SET_ARTICLE_COMMENTS_FETCHING_STATUS',
    fetching,
    fetched
  };
}

export function setRecommendedArticles(articles: Object) {
  return {
    type: 'SET_RECOMMENDED_ARTICLES',
    articles
  };
}

export function setRecommendedArticlesFetchingStatus(fetching: boolean, fetched: boolean) {
  return {
    type: 'SET_RECOMMENDED_ARTICLES_FETCHING_STATUS',
    fetching,
    fetched
  };
}

export function setRelatedQuestions(questions: Object) {
  return {
    type: 'SET_RELATED_QUESTIONS',
    questions
  };
}

export function setRelatedQuestionsFetchingStatus(fetching: boolean, fetched: boolean) {
  return {
    type: 'SET_RELATED_QUESTIONS_FETCHING_STATUS',
    fetching,
    fetched
  };
}

export function setRelatedProducts(products: Object) {
  return {
    type: 'SET_RELATED_PRODUCTS',
    products
  };
}

export function setRelatedProductsFetchingStatus(fetching: boolean, fetched: boolean) {
  return {
    type: 'SET_RELATED_PRODUCTS_FETCHING_STATUS',
    fetching,
    fetched
  };
}

export function setRelatedGroupArticles(articles: Object) {
  return {
    type: 'SET_RELATED_GROUP_ARTICLES',
    articles
  };
}

export function setRelatedGroupArticlesFetchingStatus(fetching: boolean, fetched: boolean) {
  return {
    type: 'SET_RELATED_GROUP_ARTICLES_FETCHING_STATUS',
    fetching,
    fetched
  };
}

export function setGroups(groups: Array<Object>, paging: Object) {
  return {
    type: 'SET_GROUPS',
    groups,
    paging
  };
}

export function setGroupsFetchingStatus(fetching: boolean, fetched: boolean) {
  return {
    type: 'SET_GROUPS_FETCHING_STATUS',
    fetching,
    fetched
  };
}

export function setGroupsWithFilter(groups: Array<Object>, paging: Object) {
  return {
    type: 'SET_GROUPS_WITH_FILTERS',
    groups,
    paging
  };
}

export function setGroupsFilters(filters: string) {
  return {
    type: 'SET_GROUPS_FILTERS',
    filters
  };
}

export function clearGroups() {
  return {
    type: 'CLEAR_GROUPS'
  };
}

export function setHomePodcasts(homePodcasts: Array<Object>, paging: Object, replace: boolean) {
  return {
    type: 'SET_HOME_PODCASTS',
    homePodcasts,
    paging,
    replace
  };
}

export function setHomePodcastsFetchingStatus(fetching: boolean, fetched: boolean) {
  return {
    type: 'SET_HOME_PODCASTS_FETCHING_STATUS',
    fetching,
    fetched
  };
}

export function setHomeVideos(homeVideos: Array<Object>, paging: Object, replace: boolean) {
  return {
    type: 'SET_HOME_VIDEOS',
    homeVideos,
    paging,
    replace
  };
}

export function setHomeVideosFetchingStatus(fetching: boolean, fetched: boolean) {
  return {
    type: 'SET_HOME_VIDEOS_FETCHING_STATUS',
    fetching,
    fetched
  };
}

export function setVideos(videos: Array<Object>, paging: Object, replace: boolean) {
  return {
    type: 'SET_VIDEOS',
    videos,
    paging,
    replace
  };
}

export function clearMultimedia() {
  return {
    type: 'CLEAR_VIDEOS'
  };
}

export function setVideosFetchingStatus(fetching: boolean, fetched: boolean) {
  return {
    type: 'SET_VIDEOS_FETCHING_STATUS',
    fetching,
    fetched
  };
}

export function setVideosWithFilter(videos: Array<Object>, paging: Object) {
  return {
    type: 'SET_VIDEOS_WITH_FILTERS',
    videos,
    paging
  };
}

/**
 * Fija los filtros de /videos y /podcasts.
 *
 * @param filters `{ tag, author }`. Cada uno por separado: se combinan en la consulta, asi que se
 *                puede pedir "los podcasts de Finect Alpha sobre renta fija".
 */
export function setMultimediaFilters(filters: { tag: ?string, author: ?string }) {
  return {
    type: 'SET_VIDEOS_FILTERS',
    filters
  };
}

export function postUserComment(articleId: string, comment: string, image: string, author: Object) {
  return (dispatch: Dispatch) =>
    finectAPI({
      version: 'v4',
      path: `contents/articles/${articleId}/comments`,
      method: 'POST',
      headers: {
        'x-referrer': `${window.location.href}#comentarios`
      },
      body: {
        body: {
          text: comment
        },
        image
      },
      onSuccess: ({ code, data }: { code: number, data: Object }) => {
        code && code === 302
          ? (window.location.href = data.location)
          : dispatch(setArticleUserComment(models.comment(data, [author])));
      },
      onError: () => {
        dispatch(setError({ code: 404, message: 'No se pudo realizar el comentario' }));
      }
    });
}

export function getArticleComments(article: string, limit: number) {
  return (dispatch: Dispatch) => {
    dispatch(setArticleCommentsFetchingStatus(true, true));

    return finectAPI({
      version: 'v4',
      path: `contents/articles/${article}/comments?limit=${limit || 5}`,
      method: 'GET',
      onSuccess: ({ data, paging }: { data: Object, paging: Object }) => {
        dispatch(setArticleCommentsFetchingStatus(false, true));
        dispatch(setArticleComments(models.articleComments(data.comments, data.authors), paging));
      },

      onError: () => {
        dispatch(setArticleCommentsFetchingStatus(false, true));
        dispatch(setError({ code: 404, message: 'Artículo no encontrado' }));
      }
    });
  };
}

export function getArticleCommentsPrevPage(article: string, limit: number) {
  return (dispatch: Dispatch, getState: Function) => {
    dispatch(setArticleCommentsFetchingStatus(true, true));

    const links = getState().content.articleComments.paging.links;
    const prev = links.find(link => link.rel === 'prev');

    if (prev && prev.href) {
      const offset = getUrlParameter('offset', prev.href);

      return finectAPI({
        version: 'v4',
        path: `contents/articles/${article}/comments?limit=${limit || 5}&offset=${offset}`,
        method: 'GET',
        onSuccess: ({ data, paging }: { data: Object, paging: Object }) => {
          dispatch(setArticleCommentsFetchingStatus(false, true));
          dispatch(setArticleComments(models.articleComments(data.comments, data.authors), paging));
        },

        onError: () => {
          dispatch(setArticleCommentsFetchingStatus(false, true));
          dispatch(setError({ code: 404, message: 'Error al cargar los comentarios' }));
        }
      });
    }
  };
}

export function getRecommendedArticles(cookies: string = 'include', limit: number) {
  return (dispatch: Dispatch) =>
    Promise.resolve(dispatch(setRecommendedArticlesFetchingStatus(true, true))).then(() =>
      finectAPI({
        version: 'v4',
        path: `contents/articles?filter=tag+eq+34018&orderby=evergreen&limit=${limit}`,
        credentials: cookies,
        method: 'GET',
        onSuccess: ({ data }: { data: Object }) =>
          dispatch(setRecommendedArticles(models.articles(shuffle(data.articles).slice(0, 5), data.authors))),
        onError: () => dispatch(setRecommendedArticles([]))
      }));
}

export function getRelatedGroupArticles(cookies: string = 'include', author: string, limit: number = 6) {
  return (dispatch: Dispatch) =>
    Promise.resolve(dispatch(setRelatedGroupArticlesFetchingStatus(true, true))).then(() =>
      finectAPI({
        version: 'v4',
        path: `contents/profiles/${author}/articles?limit=${limit}`,
        credentials: cookies,
        method: 'GET',
        onSuccess: ({ data }: { data: Object }) =>
          dispatch(setRelatedGroupArticles(models.articles(data.articles, data.authors))),
        onError: () => dispatch(setRelatedGroupArticles([]))
      }));
}

export function getRelatedQuestions(article: Object, cookies: string = 'include') {
  return (dispatch: Dispatch) =>
    Promise.resolve(dispatch(setRelatedQuestionsFetchingStatus(true, true))).then(() => {
      const tags = (article.socialStats && article.socialStats.tags.map(tag => `tag+eq+${tag.id}`)) || [];

      return finectAPI({
        version: 'v4',
        path: `contents/questions?limit=5${tags.length ? `&filter=${tags.join('+or+')}` : ''}`,
        credentials: cookies,
        method: 'GET',
        onSuccess: ({ data }: { data: Object }) =>
          dispatch(setRelatedQuestions(models.articles(data.questions, data.authors))),

        onError: () => dispatch(setRelatedQuestions([]))
      });
    });
}

export function getRelatedProducts(article: Object, cookies: string = 'include') {
  return (dispatch: Dispatch) => {
    dispatch(setArticleCommentsFetchingStatus(true, true));

    return finectAPI({
      version: 'v4',
      path: `contents/articles/${article.id}/products?expand=stats/performance`,
      credentials: cookies,
      method: 'GET',
      onSuccess: ({ data }: { data: Object }) => {
        dispatch(setRelatedProducts(data));
        dispatch(setRelatedProductsFetchingStatus(false, true));
      },

      onError: () => {
        dispatch(setRelatedProductsFetchingStatus(false, true));
        dispatch(setError({ code: 404, message: 'Productos no encontrados' }));
      }
    });
  };
}

/**
 * Acción que carga los grupos para /grupos
 *
 */
export function getGroups(limit: number = 24, page: number = 1, tag?: number, cookie?: string) {
  const offset = limit * (page - 1);

  return (dispatch: Dispatch, getState: Function) =>
    Promise.resolve(dispatch(setGroupsFetchingStatus(true, true))).then(() => {
      const order = getState().content.groups.request.filters.order;

      return finectAPI({
        version: 'v4',
        path: `contents/profiles/organizations?filter=features/blog+eq+true&limit=${limit || 24}${
          order ? `&orderby=${order}` : ''
        }&offset=${offset}`,
        credentials: cookie,
        method: 'GET',
        onSuccess: ({ data, paging }: { data: Array<Object>, paging: Object }) =>
          Promise.all([dispatch(setGroupsFetchingStatus(false, true)), dispatch(setGroups(data, paging))]),
        onError: () =>
          Promise.all([
            dispatch(setGroupsFetchingStatus(false, true)),
            dispatch(setError({ code: 404, message: 'Error al cargar los grupos' }))
          ])
      });
    });
}

export function getGroupsNextPage(limit: number) {
  return (dispatch: Dispatch, getState: Function) => {
    dispatch(setGroupsFetchingStatus(true, true));

    const links = getState().content.groups.paging.links;

    const next = links.find(link => link.rel === 'next');
    if (next && next.href) {
      const offset = getUrlParameter('offset', next.href);
      const order = getUrlParameter('orderby', next.href);
      return finectAPI({
        version: 'v4',
        path: `contents/profiles/organizations?filter=features/blog+eq+true&limit=${limit || 24}${
          order ? `&orderby=${order}` : ''
        }&offset=${offset}`,
        method: 'GET',
        onSuccess: ({ data, paging }: { data: Array<Object>, paging: Object }) => {
          dispatch(setGroupsFetchingStatus(false, true));
          dispatch(setGroups(data, paging));
        },

        onError: () => {
          dispatch(setGroupsFetchingStatus(false, true));
          dispatch(setError({ code: 404, message: 'Error al cargar los grupos' }));
        }
      });
    }
  };
}

export function getGroupsWithFilter(order: string, limit: number) {
  return (dispatch: Dispatch) =>
    finectAPI({
      version: 'v4',
      path: `contents/profiles/organizations?filter=features/blog+eq+true&limit=${limit || 24}${
        order ? `&orderby=${order}` : ''
      }`,
      method: 'GET',
      onSuccess: ({ data, paging }: { data: Array<Object>, paging: Object }) =>
        Promise.all([dispatch(setGroupsFetchingStatus(false, true)), dispatch(setGroupsWithFilter(data, paging))]),
      onError: () =>
        Promise.all([
          dispatch(setGroupsFetchingStatus(false, true)),
          dispatch(setError({ code: 404, message: 'Error al cargar los grupos' }))
        ])
    });
}

/**
 * Ruta canónica del listado de multimedia.
 *
 * TODAS las consultas de /videos y /podcasts se construyen aquí, con el mismo orden de cláusulas y
 * de parámetros. No es cosmética: la caché de la API (`@finect/microservice/finectCache`) hashea el
 * QUERY STRING CRUDO —`object-hash({ keys: [], params: '<query string>' })`—, así que el mismo
 * listado escrito de dos formas son dos entradas distintas en Redis, cada una con su propio fallo
 * de caché y su propia consulta de count, que no se cachea aparte.
 *
 * Y pasaba: medido en el navegador el 2026-09-09, la página 1 de una lista filtrada pedía
 * `tag+eq+4358+and+type+eq+'video'` y la página 2 `type+eq+'video'+and+tag+eq+4358`. Mismo
 * contenido, dos claves. Con el filtro en la URL habría una tercera, porque el render de servidor
 * entra por `getMultimedia` y el clic por `getMultimediaWithFilter`.
 *
 * Las dos reglas del formato canónico:
 *
 * - Cláusulas del filtro en el orden `tag, author, type`. Es el que ya emitían `getMultimedia` con
 *   tema y `getMultimediaWithFilter`, así que las entradas calientes de producción no se enfrían.
 * - Parámetros en orden alfabético —`filter, limit, offset, orderby`—, que es lo que hace
 *   `getURLWithQueryString` (usa query-string, que ordena las claves). Por eso se sigue usando esa
 *   función en vez de montar la cadena a mano: el orden lo garantiza la librería.
 *
 * El `offset` se omite cuando es 0 en lugar de mandarse a cero, porque `?offset=0` es otra clave
 * distinta de no mandarlo, para la misma primera página.
 *
 * @param type Tipo de contenido
 * @param filters `{ tag, author }`, los dos opcionales
 * @param limit Cuántos traer
 * @param offset Desplazamiento; 0 se omite
 * @param orderby Clave de orden; por defecto la cronológica del listado
 */
export function getMultimediaPath(
  type: 'video' | 'podcast',
  filters: { tag?: ?string | ?number, author?: ?string } = {},
  limit?: number,
  offset?: number,
  orderby?: string
): string {
  const { tag, author } = filters;
  const clauses = [
    ...tag ? [`tag+eq+${tag}`] : [],
    ...author ? [`author+eq+'${author}'`] : [],
    `type+eq+'${type}'`
  ];

  return getURLWithQueryString(
    'contents/articles',
    {
      filter: clauses.join('+and+'),
      limit: limit || 12,
      ...offset ? { offset } : {},
      orderby: orderby || '-revision/created'
    },
    { encode: false }
  );
}

/**
 * Acción que carga los videos para /videos
 *
 */

export function getMultimedia(
  type: 'video' | 'podcast' = 'video',
  cookies: string,
  limit?: number,
  tag?: ?(number | string),
  author?: ?string
) {
  return (dispatch: Dispatch, getState: Function) =>
    Promise.resolve(dispatch(setVideosFetchingStatus(true, true))).then(() => {
      const stored = getState().content.videos.request.filters;
      // El autor viaja aparte del tema porque el render de servidor entra por aquí con los dos, tal
      // como vengan en la URL.
      const path = getMultimediaPath(
        type,
        { tag: tag || stored.filter, author: author || stored.author },
        limit
      );

      return finectAPI({
        version: 'v4',
        method: 'GET',
        path,
        credentials: cookies,
        onSuccess: ({ data, paging }: { data: Object, paging: Object }) =>
          dispatch(setVideos(models.videos(data.articles, data.authors), paging, true)),
        onError: () => dispatch(setVideosFetchingStatus(false, true))
      });
    });
}

/**
 * Carga los imprescindibles de la portada de /videos o /podcasts.
 *
 * Son los huecos que edición rellena en Dashboard2 (Contenidos → Imprescindibles), en la misma
 * tabla que los highlights de la home con `section` = videos | podcasts. La API devuelve los
 * artículos completos en el orden de los huecos y, dentro de cada uno, `highlight`: el título o la
 * imagen propios si edición los escribió, que la tarjeta prefiere a los del artículo.
 *
 * La petición apunta para qué tipo es (`forType`): así la escena sabe si ya están pedidos sin mirar
 * si la lista trae algo.
 *
 * @param type Tipo de contenido
 * @param cookies Cookies de la petición, en render de servidor
 */
export function getMultimediaHighlights(type: 'video' | 'podcast' = 'video', cookies?: string) {
  return (dispatch: Dispatch) =>
    Promise.resolve(dispatch({ type: 'SET_MULTIMEDIA_HIGHLIGHTS_FETCHING', fetching: true, forType: type })).then(() =>
      finectAPI({
        version: 'v4',
        method: 'GET',
        path: `contents/highlights?section=${type === 'podcast' ? 'podcasts' : 'videos'}`,
        credentials: cookies,
        onSuccess: ({ data }: { data: Object }) =>
          dispatch({
            type: 'SET_MULTIMEDIA_HIGHLIGHTS',
            items: models.videos(data.articles || [], data.authors || [])
          }),
        // Un fallo aquí no puede tumbar la portada: el bloque simplemente no se pinta.
        onError: () => dispatch({ type: 'SET_MULTIMEDIA_HIGHLIGHTS', items: [] })
      }));
}

/**
 * Muestra sin filtrar de la que salen las pastillas de tema y de serie.
 *
 * Hace falta porque las opciones del filtro se derivan de los contenidos cargados, y cuando se llega
 * a la pantalla CON un filtro puesto —desde un enlace compartido, o con el botón atrás— lo cargado ya
 * está filtrado: la tira se quedaría con la pastilla activa y sin forma de volver a las demás, o
 * directamente sin tira.
 *
 * Pide exactamente la misma consulta canónica que la primera página de la portada, así que es la
 * entrada de caché más caliente de la pantalla: en la práctica no cuesta una consulta, cuesta un
 * round-trip a Redis. Y solo se pide cuando se entra con filtro; sin filtro, la propia lista sirve
 * de muestra (ver `SET_VIDEOS` en el reducer).
 *
 * @param type Tipo de contenido
 * @param cookies Cookies de la petición, en render de servidor
 * @param limit Tamaño de la muestra
 */
export function getMultimediaFacets(type: 'video' | 'podcast' = 'video', cookies?: string, limit?: number) {
  return (dispatch: Dispatch) =>
    finectAPI({
      version: 'v4',
      method: 'GET',
      path: getMultimediaPath(type, {}, limit),
      credentials: cookies,
      onSuccess: ({ data }: { data: Object }) =>
        dispatch({ type: 'SET_MULTIMEDIA_FACETS', items: models.videos(data.articles, data.authors) }),
      // Sin muestra no hay pastillas, pero la lista filtrada se pinta igual: no puede tumbar nada.
      onError: () => dispatch({ type: 'SET_MULTIMEDIA_FACETS', items: [] })
    });
}

/**
 * Acción que carga los vídeos para la home de Finect.
 *
 * Son los publicados por el usuario "__Finect".
 * Estos: https://dashboard2.finect.com/contents/articles?search=&tags=&type=video&user=__Finect
 *
 */
export function getHomeVideos(cookies: string, limit?: number) {
  return (dispatch: Dispatch) =>
    Promise.resolve(dispatch(setHomeVideosFetchingStatus(true, true))).then(() => {
      const path = getURLWithQueryString(
        'contents/articles',
        {
          orderby: '-revision/updated',
          filter: `tag+eq+2333+and+type+eq+'video'`,
          limit: limit || 12
        },
        { encode: false }
      );

      return finectAPI({
        version: 'v4',
        method: 'GET',
        path,
        credentials: cookies,
        onSuccess: ({ data, paging }: { data: Object, paging: Object }) =>
          dispatch(setHomeVideos(models.videos(data.articles, data.authors), paging, true)),
        onError: () => dispatch(setHomeVideosFetchingStatus(false, true))
      });
    });
}

export function getHomePodcasts(cookies: string, limit?: number) {
  return (dispatch: Dispatch) =>
    Promise.resolve(dispatch(setHomePodcastsFetchingStatus(true, true))).then(() => {
      const path = getURLWithQueryString(
        'contents/articles',
        {
          orderby: '-revision/updated',
          filter: `tag+eq+2333+and+type+eq+'podcast'`,
          limit: limit || 12
        },
        { encode: false }
      );

      return finectAPI({
        version: 'v4',
        method: 'GET',
        path,
        credentials: cookies,
        onSuccess: ({ data, paging }: { data: Object, paging: Object }) =>
          dispatch(setHomePodcasts(models.videos(data.articles, data.authors), paging, true)),
        onError: () => dispatch(setHomePodcastsFetchingStatus(false, true))
      });
    });
}

export function getMultimediaNextPage(type: 'video' | 'podcast' = 'video', limit: number) {
  return (dispatch: Dispatch, getState: Function) => {
    dispatch(setVideosFetchingStatus(true, true));

    const links = getState().content.videos.paging.links;
    const { filter, author } = getState().content.videos.request.filters;

    const next = links.find(link => link.rel === 'next');
    if (next && next.href) {
      const offset = getUrlParameter('offset', next.href);

      return finectAPI({
        version: 'v4',
        // Por el constructor canónico, que arrastra los DOS filtros. El autor se quedaba fuera, así
        // que la página 2 de «los episodios de Finect Alpha» habría traído los de todo el mundo; y
        // las cláusulas iban en otro orden que en la página 1, o sea otra entrada de caché para el
        // mismo listado. La clave de orden también sale de ahí: cambiarla a mitad de paginación
        // reordena el conjunto entero bajo el desplazamiento, y la página 2 traía huecos.
        path: getMultimediaPath(type, { tag: filter, author }, limit, Number(offset)),
        method: 'GET',
        onSuccess: ({ data, paging }: { data: Object, paging: Object }) => {
          dispatch(setVideosFetchingStatus(false, true));
          dispatch(setVideos(models.videos(data.articles, data.authors), paging, false));
        },

        // Una tanda que falla no es un 404 de la pagina: lo que hay cargado sigue ahi y sigue siendo
        // navegable. Y menos ahora, que la peticion la dispara el scroll y no un clic: un fallo
        // pasajero levantaba la pantalla de error sin que nadie hubiera pedido nada. Al volver a
        // cruzar el ancla —o con el boton— se reintenta.
        onError: () => dispatch(setVideosFetchingStatus(false, true))
      });
    }
  };
}

/**
 * Carga el listado con los filtros aplicados.
 *
 * El autor va entre comillas y el tema no: el tema es un id numerico y el autor es la cadena hex
 * que devuelve el propio listado en `article.author`. El campo `author` del filtro lo mapea
 * api-v4-contents a `COALESCE(hex(g.IdGroup), p.AUTHOR_ID)`, que es exactamente lo que emite el
 * select, asi que se le devuelve el mismo valor que nos dio.
 *
 * @param type Tipo de contenido
 * @param filters `{ tag, author }`, cualquiera de los dos opcional
 * @param limit Cuantos traer
 */
export function getMultimediaWithFilter(
  type: 'video' | 'podcast' = 'video',
  filters: { tag: ?string, author: ?string } = { tag: null, author: null },
  limit: number
) {
  return (dispatch: Dispatch) =>
    finectAPI({
      version: 'v4',
      // Por el constructor canónico: la misma cadena que emite `getMultimedia` para este mismo
      // listado, así que el filtro que llega en la URL (render de servidor) y el que se aplica al
      // pulsar una pastilla comparten entrada de caché en vez de duplicarla.
      path: getMultimediaPath(type, filters, limit),
      method: 'GET',
      onSuccess: ({ data, paging }: { data: Object, paging: Object }) =>
        Promise.all([
          dispatch(setVideosFetchingStatus(false, true)),
          dispatch(setVideosWithFilter(models.videos(data.articles, data.authors), paging))
        ]),
      // Un filtro que no se puede servir NO es un 404 de la pagina: la pagina esta ahi y sigue
      // siendo navegable. Antes se levantaba el error global y la pantalla no decia nada, asi que
      // el filtro se quedaba marcado sobre la lista anterior. Ahora lo cuenta y ofrece reintentar.
      onError: () => dispatch({ type: 'SET_VIDEOS_FILTER_ERROR' })
    });
}

export function getTags() {
  return function (dispatch: Dispatch) {
    return finectAPI({
      version: 'v4',
      path: `contents/tags?limit=50`,
      method: 'GET',
      onSuccess: ({ data }: { data: Array<Object> }) => dispatch(setTags(data)),
      onError: () => Promise.all([dispatch(setError({ code: 404, message: 'Error al cargar los temas' }))])
    });
  };
}

export function setTagContents(contents: Array<Object>, paging: Object, add: boolean) {
  return {
    type: ACTIONS.SET_TAG_CONTENTS,
    contents,
    paging,
    add
  };
}

export function setTagLatestContents(contents: Array<Object>) {
  return {
    type: ACTIONS.SET_TAG_LATEST_CONTENTS,
    contents
  };
}

export function setAdvisersProfiles(profiles: Array<Object>) {
  return {
    type: 'SET_ADVISERS_PROFILES',
    profiles
  };
}

export function setContentsFetchingStatus(fetching: boolean) {
  return {
    type: ACTIONS.SET_TAG_CONTENTS_FETCHING_STATUS,
    fetching
  };
}

export function setTagLatestContentsFetchingStatus(fetching: boolean) {
  return {
    type: ACTIONS.SET_TAG_LATEST_CONTENTS_FETCHING_STATUS,
    fetching
  };
}

/**
 * Setea los vídeos asociados a una tag
 *
 * @param {*} tagId
 * @param {*} videos
 */
export function setTagVideos(tagId: string, videos: Array<Object>) {
  return {
    type: ACTIONS.SET_TAG_VIDEOS,
    tagId,
    videos
  };
}

/**
 * Obtiene los vídeos de una tag
 *
 * @param {*} tagId
 * @returns
 */
export function getTagVideos(tagId: string) {
  return function (dispatch: Dispatch) {
    return Promise.resolve(dispatch(setTagContentsFetchingStatus('videos', tagId, true, false))).then(() =>
      finectAPI({
        version: 'v4',
        // TODO: Ordenar por evergreen cuando se pueda editar desde el dashboard
        path: `contents/articles?filter=tag+eq+${tagId}+and+type+eq+'video'&orderby=-revision/updated`,
        method: 'GET',
        onSuccess: ({ data }: { data: Object }) => dispatch(setTagVideos(tagId, data.articles))
      }));
  };
}

const getModelsByType = (contents, authors) =>
  contents
    .map((content) => {
      if (['article', 'video', 'external-link', 'podcast'].includes(content.type)) {
        return models.article(content, authors);
      }

      if (content.type === 'question') {
        return models.question(content, authors);
      }

      return null;
    })
    .filter(item => item);

/**
 * Obtiene contenidos asociados a una tag
 *
 * En principio esta acción es la que carga contenidos ordenados
 * descendentemente en /temas/:alias
 *
 * @param {¿number?} tag Id de la tag
 * @param {*} limit Número de elementos a cargar
 */
export function getTagContents(tag: number, page: number, withEvergreen?: boolean, limit?: number, cookie?: string) {
  const query = {
    filter: `tag+eq+${tag}`,
    limit: limit || 10,
    offset: (limit || 10) * (page - 1),
    orderby: `${withEvergreen ? 'evergreen,' : ''}-revision/created`
  };

  return function (dispatch: Dispatch) {
    return Promise.resolve(dispatch(setContentsFetchingStatus(true))).then(() =>
      finectAPI({
        version: 'v4',
        path: getURLWithQueryString('contents', query, { encode: false }),
        method: 'GET',
        credentials: cookie,
        onSuccess: ({ data, paging }: { data: Object, paging: Object }) =>
          dispatch(setTagContents(getModelsByType(data.contents || [], data.authors || []), paging, false)),
        onError: () =>
          Promise.all([dispatch(setError({ code: 404, message: 'Error al cargar los contenidos de la tag' }))])
      }));
  };
}

export function getTagContentsNextPage(tag: number, limit?: number, next: Object) {
  return function (dispatch: Dispatch) {
    return Promise.resolve(dispatch(setContentsFetchingStatus(true))).then(() => {
      if (next && next.href) {
        const offset = getUrlParameter('offset', next.href);

        const query = {
          filter: `tag+eq+${tag}`,
          limit: limit || 10,
          offset,
          orderby: '-revision/created'
        };

        return finectAPI({
          version: 'v4',
          path: getURLWithQueryString('contents', query, { encode: false }),
          method: 'GET',
          onSuccess: ({ data, paging }: { data: Object, paging: Object }) =>
            dispatch(setTagContents(getModelsByType(data.contents || [], data.authors || []), paging, true)),
          onError: () =>
            Promise.all([dispatch(setError({ code: 404, message: 'Error al cargar los contenidos de la tag' }))])
        });
      }
    });
  };
}

export function getTagLatestContents(tag: number, page: number, limit?: number, cookie?: string, tagExclude?: number) {
  const query = {
    filter: `tag+eq+${tag}${tagExclude ? `+and+tag+ne+${tagExclude}` : ''}`,
    limit: limit || 10,
    offset: (limit || 10) * (page - 1),
    orderby: '-revision/created'
  };

  return function (dispatch: Dispatch) {
    return Promise.resolve(dispatch(setTagLatestContentsFetchingStatus(true))).then(() =>
      finectAPI({
        version: 'v4',
        path: getURLWithQueryString(`contents/articles`, query, { encode: false }),
        method: 'GET',
        credentials: cookie,
        onSuccess: ({ data }: { data: Object }) =>
          dispatch(setTagLatestContents(getModelsByType(data.articles || [], data.authors || []))),
        onError: () =>
          Promise.all([dispatch(setError({ code: 404, message: 'Error al cargar los últimos contenidos de la tag' }))])
      }));
  };
}

export function getAdvisersProfiles(
  filter: { tag?: number | string, subtype?: string },
  order: string = 'evergreen',
  limit: number = 10
) {
  const { tag, subtype } = filter;

  const filters = [];

  if (tag) filters.push(`tag+eq+${tag}`);
  if (subtype) filters.push(`subtype+eq+'${subtype}'`);

  const filterPath = filters.length ? `?filter=${filters.join('+and+')}` : '';
  const limitPath = limit ? `${filterPath ? '&' : '?'}limit=${limit}` : '';

  return function (dispatch: Dispatch) {
    return finectAPI({
      version: 'v4',
      path: `/contents/profiles/advisers${filterPath}${limitPath}${order ? `&orderby=${order}` : ''}`,
      method: 'GET',
      // Sin la ficha de cada asesor: este slice solo lo pinta la home (AdvisersBlock), y viaja en su
      // estado inicial
      onSuccess: ({ data }: { data: Array<Object> }) => dispatch(setAdvisersProfiles(data.map(lightAuthor))),
      onError: () =>
        Promise.all([dispatch(setError({ code: 404, message: 'Error al cargar los contenidos de la tag' }))])
    });
  };
}

/**
 * Carga contenidos asociados a una tag
 *
 * @param {Object} tag Modelo de tag
 *
 */
export function getTagMainContents(tag: Object, page?: number = 1, withEvergreen?: boolean, cookie?: string) {
  return function (dispatch: Function) {
    const promises = [
      dispatch(getTagContents(tag.id, page, withEvergreen, 30, cookie)),
      dispatch(getTagLatestContents(tag.id, page, 20, cookie)),

      dispatch(getTagVideos(tag.id)),

      // getProducts está en products
      dispatch(getProducts(`tag-${tag.alias}`, { tag: tag.id, limit: 20 })),

      // Por qué ???
      dispatch(getAdvisersProfiles({ tag: tag.id }))
    ];

    return Promise.all(promises);
  };
}

/**
 * Obtiene una tag y su contenido asociado:
 *
 * - Productos
 * - Contenido
 * - Perfiles de asesores
 */

export function getTagWithContents(tag: string | number, page?: number = 1, withEvergreen?: boolean, cookie?: string) {
  return function (dispatch: Function, getState: Function) {
    return dispatch(getTag(`${tag}`)).then(() => {
      const { model } = getState().content.tag;

      const promises = model ? [dispatch(getTagMainContents(model, page, withEvergreen, cookie))] : [];

      return Promise.all(promises);
    });
  };
}

export function getTagWithLatestContents(tag: string, page?: number = 1, cookie?: string, tagExclude?: number) {
  return function (dispatch: Function, getState: Function) {
    return dispatch(getTag(`${tag}`)).then(() => {
      const { model } = getState().content.tag;

      const promises = model ? [dispatch(getTagLatestContents(model.id, page, 10, cookie, tagExclude))] : [];

      return Promise.all(promises);
    });
  };
}

// Questions
export function setQuestions(id: string, request: Object, questions: Array<Object>) {
  return {
    id,
    type: 'SET_QUESTIONS',
    items: questions,
    request
  };
}

export function getProductQuestions(
  productId: string,
  offset: number = 0
  /* filter: Object */
) {
  const id = `product-id-${productId}`;

  const request = {
    offset,
    filter: { productId }
  };

  return function (dispatch: Dispatch) {
    return finectAPI({
      version: 'v4',
      path: `contents/products/${productId}/questions`,
      method: 'GET',
      onSuccess: ({
        data
      }: {
        code: number,
        data: {
          questions: Array<Object>,
          authors: Array<Object>
        }
      }) => {
        const questions = models.questions(data.questions, data.authors);

        const promises = [dispatch(setQuestions(id, request, questions))];

        return Promise.all(promises);
      },
      onError: () => {
        const promises = [dispatch(setQuestions(id, request, []))];

        return Promise.all(promises);
      }
    });
  };
}

// REVIEWS
export function setReviews(id: string, request: Object, reviews: Array<Object>, paging: Object) {
  return {
    id,
    type: 'SET_REVIEWS',
    items: reviews,
    request,
    paging
  };
}

export function setReview(id: string, request: Object, reviews: Array<Object>) {
  return {
    id,
    type: 'SET_REVIEWS',
    items: reviews,
    request: {},
    paging: {}
  };
}

export function setReviewsFetchingStatus(id: string, fetching: boolean, fetched: boolean, request: Object) {
  return {
    type: 'SET_REVIEWS_FETCHING_STATUS',
    id,
    fetching,
    fetched,
    request
  };
}

type ProductReviewsOptions = {
  offset?: number,
  limit?: number,
  score?: 1 | 2 | 3 | 4 | 5 | null,
  orderBy?: ReviewsOrderBy,
  productId: string,
  // Solo la carga inicial (destacada) sube los reviews de profesionales arriba.
  // Cuando el usuario ordena/filtra, mostramos el resultado tal cual llega.
  featured?: boolean
};

export function getProductReviews(params: ProductReviewsOptions) {
  const { productId } = params;
  const id = `product-id-${productId}`;

  const defaultParams = {
    offset: 0,
    limit: 25,
    score: null,
    orderBy: '-score',
    featured: false
  };

  const request = { ...defaultParams, ...params };

  return function (dispatch: Dispatch) {
    return Promise.all([dispatch(setReviewsFetchingStatus(id, true, true, request))]).then(() =>
      finectAPI({
        version: 'v4',
        path: `contents/products/${productId}/reviews?limit=${request.limit}&offset=${request.offset}&orderby=${
          request.orderBy
        }${request.score ? `&filter=score+eq+'${request.score}'` : ''}`,
        method: 'GET',
        onSuccess: ({ data, paging }: { data: Object, paging: Object }) => {
          const reviews = models.reviews(data.reviews, data.authors);
          const promises = [];
          if (request.featured && request.orderBy === '-score') {
            // show first reviews whose author has an organization with roles 'adviser'
            const orderedReviews = reviews.sort((itemA, itemB) => {
              const aHasAdviser = itemA.author?.organizations?.some(org => org.roles.includes('adviser'));
              const bHasAdviser = itemB.author?.organizations?.some(org => org.roles.includes('adviser'));
              if (aHasAdviser && !bHasAdviser) return -1;
              if (!aHasAdviser && bHasAdviser) return 1;
              return 0;
            });

            promises.push(dispatch(setReviews(id, request, orderedReviews, paging)));
          } else {
            promises.push(dispatch(setReviews(id, request, reviews, paging)));
          }
          return Promise.all(promises);
        },
        onError: () => {
          const promises = [dispatch(setReviews(id, request, []))];

          return Promise.all(promises);
        }
      }));
  };
}

export function getProductReview(params: { reviewId: string }) {
  const { reviewId } = params;
  const id = `review-id-${reviewId}`;

  const request = { ...{}, ...params };

  return (dispatch: Dispatch) =>
    Promise.all([dispatch(setReviewsFetchingStatus(id, true, true, request))]).then(() =>
      finectAPI({
        version: 'v4',
        path: `contents/reviews/${reviewId}`,
        method: 'GET',
        onError: () => {
          const promises = [dispatch(setReview(id, request, []))];

          return Promise.all(promises);
        },
        onSuccess: ({ data }: { data: Object }) => {
          const review = models.review(data.review, data.authors);

          const promises = [dispatch(setReview(id, request, [review]))];

          return Promise.all(promises);
        }
      }).finally(() => Promise.all([dispatch(setReviewsFetchingStatus(id, false, true, request))])));
}

export function setArticles(id: string, contents: Array<Object>, paging: Object) {
  return {
    type: 'SET_ARTICLES_TAG',
    id,
    contents,
    paging
  };
}

export function setArticlesFetchingStatus(id: string, fetching: boolean) {
  return {
    type: 'SET_ARTICLES_TAG_FETCHING_STATUS',
    id,
    fetching
  };
}

/**
 * Carga los artículos listables de un escaparate
 *
 * @param {*} tag
 * @param {*} limit
 * @param {*} orderby
 * @param {*} operator operador con el que se unen las tags cuando se pasa un array
 */
export function getArticles(tag: number | Array<number>, limit?: number, orderby?: string, operator?: string = 'or') {
  return (dispatch: Dispatch) =>
    Promise.resolve(
      dispatch(setArticlesFetchingStatus(typeof tag === 'object' ? `${tag.map(t => t).join('-')}` : `${tag}`, true))
    ).then(() =>
      finectAPI({
        version: 'v4',
        path: getURLWithQueryString(
          'contents/articles',
          {
            filter:
              typeof tag === 'object'
                ? `tag+eq+${tag.join(`+${operator}+tag${operator === 'and' ? '2' : ''}+eq+`)}`
                : `tag+eq+${tag}`,
            limit: limit || 10,
            orderby: orderby || 'evergreen,-revision/updated'
          },
          { encode: false }
        ),
        method: 'GET',
        onSuccess: ({ data, paging }: { data: Object, paging: Object }) =>
          dispatch(
            setArticles(
              typeof tag === 'object' ? `${tag.map(t => t).join('-')}` : `${tag}`,
              models.articles(data.articles, data.authors),
              paging
            )
          ),
        onError: () =>
          Promise.all([dispatch(setError({ code: 404, message: 'Error al cargar los contenidos de la tag' }))])
      }));
}

// TODO: Las acciones son distintas pero el reducer no
export function getProductArticles(productId: string) {
  return (dispatch: Dispatch) => {
    dispatch(setArticlesFetchingStatus(productId, true));

    return finectAPI({
      version: 'v4',
      path: `contents/products/${productId}/articles`,
      method: 'GET',
      onSuccess: ({ data, paging }: { data: Object, paging: Object }) =>
        dispatch(setArticles(productId, models.articles(data.articles, data.authors), paging)),
      onError: () =>
        Promise.all([dispatch(setError({ code: 404, message: 'Error al cargar los contenidos del producto' }))])
    });
  };
}

//* grupos enterprise gome  */

export function setEnterpriseAdvisers(enterprises: Array<Object>) {
  return {
    type: 'SET_ENTERPRISE_ADVISERS',
    enterprises
  };
}

export function getEnterpriseAdvisers() {
  return (dispatch: Dispatch, getState: Function) => {
    const enterprisesFetched = getState().content.enterpriseAdvisers.fetched;
    if (enterprisesFetched) return Promise.resolve();

    // Se devuelve la promesa para que el render de servidor espere la respuesta: sin ella podía llegar
    // después de pintar, y la home salía con los huecos de carga en lugar de los logos.
    return finectAPI({
      version: 'v4',
      path: `contents/profiles/organizations?filter=features/enterprise+eq+true+and+tag+eq+34490&orderby=evergreen`,
      method: 'GET',
      // Sin avisos legales ni redes: solo los pinta la home (AdvisersBlock), y viajan en su estado inicial
      onSuccess: ({ data }: { data: Array<Object> }) => dispatch(setEnterpriseAdvisers(data.map(lightOrganization))),
      onError: () =>
        Promise.all([dispatch(setError({ code: 404, message: 'Error al cargar los contenidos de la tag' }))])
    });
  };
}

export function setBanksFetchingStatus(fetching: boolean) {
  return {
    type: 'SET_BANKS_FETCHING_STATUS',
    fetching
  };
}

export function setBanks(banks: Array<Object>) {
  return {
    type: 'SET_BANKS',
    banks
  };
}

export function clearBanks() {
  return {
    type: 'CLEAR_BANKS'
  };
}

export function getBanks(tagId: number, limit?: number) {
  return function (dispatch: Dispatch) {
    dispatch(setBanksFetchingStatus(true));

    return finectAPI({
      version: 'v4',
      method: 'GET',
      path: getURLWithQueryString(
        'contents/profiles/publics/organizations',
        {
          filter: `tag+eq+${tagId}`,
          limit: limit || undefined,
          orderby: 'evergreen'
        },
        { encode: false }
      ),
      onSuccess: ({ data }: { data: Array<Object> }) => dispatch(setBanks(data)),
      onError: () => dispatch(setBanksFetchingStatus(false))
    });
  };
}

/**
 * Acción que carga un artículo a partir del ID del autor y su identificador
 *
 * @param {string} profile ID del profile
 * @param {string} article ID (o alias) del artículo
 *
 */
export function getArticle(cookies: string, profile: string, article: string) {
  return function (dispatch: Dispatch) {
    return finectAPI({
      version: 'v4',
      path: `contents/${profile ? `profiles/${profile}/` : ''}articles/${article}`,
      credentials: cookies,
      method: 'GET',
      onSuccess: async ({ data }: { data: Object }) => {
        let widgets = null;
        if (data.article.widgets.length) {
          await dispatch(getWidgets()).then((result) => {
            widgets = result;
          });
        }

        dispatch(setArticle(models.article(data.article, [data.author], widgets)));
        return models.article(data.article, [data.author]);
      },
      onError: () => dispatch(setArticleNotFound())
    });
  };
}

/*
  Carga de un artículo: el artículo y lo imprescindible para pintarlo (el ajuste de la píldora de
  asesores, los ajustes de tag-content de sus tags y, en cliente, las preguntas relacionadas).

  Aquí ya NO se piden ni el feed del aside ni los artículos siguientes del scroll infinito. Los pide la
  propia página en cliente (ArticleScroll: scheduleNextArticlesPrefetch y handleLoadSidebarArticles)
  porque la primera pantalla no los usa y, pedidos en servidor, viajaban enteros dentro del estado
  inicial: 20 artículos de feed con su cuerpo para pintar cuatro títulos y 10 siguientes con su cuerpo
  para un scroll al que la mayoría no llega. Medido en un artículo largo: 480 KB de JSON de más.
*/
export function fetchArticle(
  cookies: string = 'include',
  subtype: 'usuario' | 'grupos',
  profile: string,
  alias: string,
  withRecommendedArticles: boolean = true
) {
  return function (dispatch: Dispatch) {
    const promises = [];
    promises.push(dispatch(getArticle(cookies, profile, alias)));
    promises.push(dispatch(fetchSetting('articles-pill-advisers-leads')));

    withRecommendedArticles && promises.push(dispatch(getRecommendedArticles(cookies, 25)));

    return Promise.all(promises).then((results) => {
      const [article] = results;
      const postPromises = [];

      if (article) {
        const { author = {} } = article;

        const showTagContent = !['company'].includes(author?.subtype) || aliasWithTagContent.includes(alias);
        if (showTagContent) {
          (article.socialStats?.tags || []).forEach((tag) => {
            postPromises.push(dispatch(fetchSetting(`articles-tag-${tag.id}`)));
          });
        }

        isBrowser() && postPromises.push(dispatch(getRelatedQuestions(article, cookies)));
      }

      return Promise.all(postPromises).then(() => article);
    });
  };
}
